<template>
    <v-dialog
        :model-value="modelValue"
        max-width="1200"
        width="95vw"
        :fullscreen="$vuetify.display.smAndDown"
        scrollable
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card>
            <v-toolbar>
                <v-toolbar-title>
                    Actualizare în masă
                    ({{ selectedProductCodes.length }} / {{ matchedTotalCount }})
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="saving || applying || isApplyBuildBusy"
                    @click="close"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <div class="text-subtitle-2 mb-2">Filtre</div>
                <v-row dense>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="criteria.search"
                            label="Căutare (cod / denumire produs și furnizor)"
                            density="compact"
                            variant="outlined"
                            clearable
                            hide-details="auto"
                            prepend-inner-icon="mdi-magnify"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <HierarchicalFilterSelect
                            v-model:parent="criteria.systems"
                            v-model:child="criteria.systemSubcategory"
                            label="Sistem / Subsistem"
                            parent-label="Sistem"
                            child-label="Subsistem"
                            :parent-items="categoryOptions"
                            :child-items="subcategoryOptions"
                            :child-loading="loadingSubcategories"
                            multiple
                            @parent-change="onSystemsFilterChange"
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-autocomplete
                            v-model="criteria.manufacturerCode"
                            :items="manufacturerOptions"
                            item-title="title"
                            item-value="value"
                            label="Producător"
                            density="compact"
                            variant="outlined"
                            clearable
                            hide-details="auto"
                            :custom-filter="diacriticInsensitiveFilter"
                            auto-select-first
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-autocomplete
                            v-model="criteria.furnizorLabel"
                            :items="furnizorFilterOptions"
                            label="Furnizor"
                            density="compact"
                            variant="outlined"
                            clearable
                            hide-details="auto"
                            :custom-filter="diacriticInsensitiveFilter"
                            auto-select-first
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center ga-1">
                            <div class="flex-grow-1">
                                <HierarchicalFilterSelect
                                    v-model:parent="criteria.chapterCode"
                                    v-model:child="criteria.subchapterCode"
                                    label="Categorie / Subcategorie"
                                    parent-label="Categorie"
                                    child-label="Subcategorie"
                                    :parent-items="chapterOptions"
                                    :child-items="subchapterOptions"
                                    multiple
                                    @parent-change="onChapterFilterChange"
                                />
                            </div>
                            <v-menu
                                v-if="!activeExtraFilters.length"
                                v-model="addFilterMenuOpen"
                                :close-on-content-click="false"
                                location="bottom start"
                            >
                                <template #activator="{ props: menuProps }">
                                    <v-btn
                                        v-bind="menuProps"
                                        icon
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        :disabled="!availableExtraFilterOptions.length"
                                    >
                                        <v-icon>mdi-plus</v-icon>
                                        <v-tooltip activator="parent" location="top">
                                            Adaugă filtru
                                        </v-tooltip>
                                    </v-btn>
                                </template>
                                <v-card min-width="320" elevation="8">
                                    <v-card-text class="pt-4">
                                        <v-autocomplete
                                            v-model="filterToAdd"
                                            :items="availableExtraFilterOptions"
                                            item-title="title"
                                            item-value="value"
                                            label="Proprietate de filtrat"
                                            density="compact"
                                            variant="outlined"
                                            hide-details="auto"
                                            :custom-filter="diacriticInsensitiveFilter"
                                            auto-select-first
                                            @update:model-value="onFilterToAdd"
                                        />
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                        </div>
                    </v-col>
                    <v-col
                        v-for="(filterKey, filterIndex) in activeExtraFilters"
                        :key="`extra-filter-${filterKey}`"
                        cols="12"
                        md="3"
                    >
                        <div class="d-flex align-center ga-1">
                            <div class="flex-grow-1">
                                <v-select
                                    v-if="getBulkField(filterKey)?.valueType === 'boolean'"
                                    v-model="criteria[filterKey]"
                                    :items="booleanFilterOptions"
                                    item-title="title"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(filterKey)?.valueType === 'select'"
                                    v-model="criteria[filterKey]"
                                    :items="getBulkField(filterKey)?.options || []"
                                    item-title="title"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(filterKey)?.valueType === 'measurementUnit'"
                                    v-model="criteria[filterKey]"
                                    :items="measurementUnitOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(filterKey)?.valueType === 'manufacturer'"
                                    v-model="criteria[filterKey]"
                                    :items="manufacturerOptions"
                                    item-title="title"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(filterKey)?.valueType === 'chapter'"
                                    v-model="criteria[filterKey]"
                                    :items="chapterOptions"
                                    item-title="title"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(filterKey)?.valueType === 'systems'"
                                    v-model="criteria[filterKey]"
                                    :items="categoryOptions"
                                    item-title="title"
                                    item-value="value"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    multiple
                                    chips
                                    closable-chips
                                    clearable
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                />
                                <v-text-field
                                    v-else-if="getBulkField(filterKey)?.valueType === 'percent'"
                                    v-model="criteria[filterKey]"
                                    :label="`${getBulkField(filterKey)?.label || filterKey} (%)`"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-else
                                    v-model="criteria[filterKey]"
                                    :label="getBulkField(filterKey)?.label || filterKey"
                                    density="compact"
                                    variant="outlined"
                                    clearable
                                    hide-details="auto"
                                />
                            </div>
                            <v-menu
                                v-if="filterIndex === activeExtraFilters.length - 1"
                                v-model="addFilterMenuOpen"
                                :close-on-content-click="false"
                                location="bottom start"
                            >
                                <template #activator="{ props: menuProps }">
                                    <v-btn
                                        v-bind="menuProps"
                                        icon
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        :disabled="!availableExtraFilterOptions.length"
                                    >
                                        <v-icon>mdi-plus</v-icon>
                                        <v-tooltip activator="parent" location="top">
                                            Adaugă filtru
                                        </v-tooltip>
                                    </v-btn>
                                </template>
                                <v-card min-width="320" elevation="8">
                                    <v-card-text class="pt-4">
                                        <v-autocomplete
                                            v-model="filterToAdd"
                                            :items="availableExtraFilterOptions"
                                            item-title="title"
                                            item-value="value"
                                            label="Proprietate de filtrat"
                                            density="compact"
                                            variant="outlined"
                                            hide-details="auto"
                                            :custom-filter="diacriticInsensitiveFilter"
                                            auto-select-first
                                            @update:model-value="onFilterToAdd"
                                        />
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                :aria-label="`Elimină filtrul ${getBulkField(filterKey)?.label || filterKey}`"
                                @click="removeExtraFilter(filterKey)"
                            >
                                <v-icon size="small">mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>

                <v-divider class="my-4" />

                <div class="d-flex align-center mb-2">
                    <div class="text-subtitle-2">Proprietăți de actualizat</div>
                    <v-spacer />
                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="primary"
                        :disabled="!availableEditFieldOptions.length"
                        aria-label="Adaugă proprietate"
                        @click="addEditRow"
                    >
                        <v-icon>mdi-plus</v-icon>
                        <v-tooltip activator="parent" location="top">
                            Adaugă proprietate
                        </v-tooltip>
                    </v-btn>
                </div>
                <v-row
                    v-for="(editRow, editIndex) in editRows"
                    :key="editRow.id"
                    dense
                    class="mb-1"
                >
                    <v-col cols="12" md="5">
                        <v-autocomplete
                            :model-value="editRow.fieldKey"
                            :items="editableFieldOptionsForRow(editIndex)"
                            item-title="title"
                            item-value="value"
                            label="Proprietate"
                            density="compact"
                            variant="outlined"
                            hide-details="auto"
                            :custom-filter="diacriticInsensitiveFilter"
                            auto-select-first
                            @update:model-value="onEditFieldKeyChange(editIndex, $event)"
                        />
                    </v-col>
                    <v-col cols="12" md="5">
                        <div class="d-flex align-center ga-1">
                            <v-select
                                v-if="showsApplyModeSelect(editRow.fieldKey)"
                                v-model="editRow.applyMode"
                                :items="applyModeOptions"
                                item-title="title"
                                item-value="value"
                                label="Mod"
                                density="compact"
                                variant="outlined"
                                hide-details="auto"
                                style="max-width: 160px"
                            />
                            <div class="flex-grow-1">
                                <v-select
                                    v-if="getBulkField(editRow.fieldKey)?.valueType === 'boolean'"
                                    v-model="editRow.value"
                                    :items="booleanEditOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                />
                                <v-combobox
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'select'
                                        && getBulkField(editRow.fieldKey)?.allowCustom"
                                    v-model="editRow.value"
                                    :items="getBulkField(editRow.fieldKey)?.options || []"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    placeholder="Selectează sau scrie…"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    clearable
                                />
                                <v-select
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'select'"
                                    v-model="editRow.value"
                                    :items="getBulkField(editRow.fieldKey)?.options || []"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                />
                                <v-select
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'measurementUnit'"
                                    v-model="editRow.value"
                                    :items="measurementUnitOptions"
                                    item-title="text"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'manufacturer'"
                                    v-model="editRow.value"
                                    :items="manufacturerOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-autocomplete
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'chapter'"
                                    v-model="editRow.value"
                                    :items="chapterOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    :custom-filter="diacriticInsensitiveFilter"
                                    auto-select-first
                                />
                                <v-select
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'systems'"
                                    v-model="editRow.value"
                                    :items="categoryOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    multiple
                                    chips
                                    closable-chips
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-else-if="getBulkField(editRow.fieldKey)?.valueType === 'percent'"
                                    :model-value="editRow.value"
                                    :key="`bulk-num-${editRow.id}-${editRow._numericRev || 0}`"
                                    label="Valoare nouă (%)"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    inputmode="decimal"
                                    @keydown="(event) => onBulkNumericKeydown(event, editIndex)"
                                    @update:model-value="(value) => onBulkNumericValueInput(editIndex, value)"
                                />
                                <v-text-field
                                    v-else-if="isMultiplyPercentEdit(editRow)"
                                    :model-value="editRow.value"
                                    :key="`bulk-num-${editRow.id}-${editRow._numericRev || 0}`"
                                    label="Majorare / micșorare (%)"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    inputmode="decimal"
                                    hint="Ex: 10 = +10%, -5 = -5%"
                                    persistent-hint
                                    @keydown="(event) => onBulkNumericKeydown(event, editIndex)"
                                    @update:model-value="(value) => onBulkNumericValueInput(editIndex, value)"
                                />
                                <v-text-field
                                    v-else-if="isBulkNumericValueField(editRow)"
                                    :model-value="editRow.value"
                                    :key="`bulk-num-${editRow.id}-${editRow._numericRev || 0}`"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                    inputmode="decimal"
                                    @keydown="(event) => onBulkNumericKeydown(event, editIndex)"
                                    @update:model-value="(value) => onBulkNumericValueInput(editIndex, value)"
                                />
                                <v-text-field
                                    v-else
                                    v-model="editRow.value"
                                    label="Valoare nouă"
                                    density="compact"
                                    variant="outlined"
                                    hide-details="auto"
                                />
                            </div>
                            <v-btn
                                v-if="editRows.length > 1"
                                icon
                                size="small"
                                variant="text"
                                :aria-label="`Elimină proprietatea ${getBulkField(editRow.fieldKey)?.label || editRow.fieldKey}`"
                                @click="removeEditRow(editIndex)"
                            >
                                <v-icon size="small">mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>

                <v-text-field
                    v-model="effectiveNote"
                    class="mt-3"
                    label="Notă / data efectivă (opțional)"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    hint="Se adaugă la notele furnizorului (ex: Majorare +10% de la 01.10.2026)"
                    persistent-hint
                    clearable
                />

                <v-alert
                    v-if="skippedUnchangedCount"
                    class="mt-3"
                    type="info"
                    variant="tonal"
                    density="compact"
                >
                    {{ skippedUnchangedCount }} produs(e) au deja această valoare
                    — prețul rămâne neschimbat.
                </v-alert>
                <v-alert
                    v-if="skippedIncompatibleCount"
                    class="mt-3"
                    type="info"
                    variant="tonal"
                    density="compact"
                >
                    {{ skippedIncompatibleCount }} produs(e) vor fi omise
                    (fără preț sau incompatibile).
                </v-alert>
                <v-alert
                    v-if="calculationRestrictedProducts.length"
                    class="mt-3"
                    type="warning"
                    variant="tonal"
                    density="compact"
                >
                    {{ calculationRestrictedMessage }}
                </v-alert>
                <v-alert
                    v-if="markupReferenceLockedItems.length"
                    class="mt-3"
                    type="warning"
                    variant="tonal"
                    density="compact"
                >
                    {{ markupReferenceLockedMessage }}
                </v-alert>

                <v-progress-linear
                    v-if="loadingCandidates"
                    class="mt-3"
                    indeterminate
                    color="primary"
                    height="3"
                />
                <div v-if="activeProgress" class="mt-3">
                    <div class="text-caption text-medium-emphasis mb-1">
                        {{ activeProgress.message }}
                        <span v-if="activeProgress.total">
                            ({{ activeProgress.current }} / {{ activeProgress.total }})
                        </span>
                    </div>
                    <v-progress-linear
                        :model-value="activeProgressPercent"
                        color="primary"
                        height="4"
                        rounded
                    />
                </div>

                <div class="d-flex flex-wrap align-center justify-space-between ga-2 mt-4 mb-2">
                    <div class="text-subtitle-2 mb-0">
                        Produse
                        <span class="text-medium-emphasis font-weight-regular">
                            ({{ selectedProductCodes.length }} / {{ matchedTotalCount }})
                        </span>
                    </div>
                    <div class="d-flex flex-wrap ga-2">
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!pageMatchedItems.length || loadingCandidates"
                            @click="selectPageProducts"
                        >
                            Selectează pagina
                        </v-btn>
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!matchedTotalCount || loadingCandidates || selectingAllMatching"
                            :loading="selectingAllMatching"
                            @click="selectAllMatchingProducts"
                        >
                            Selectează toate
                        </v-btn>
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!selectedProductCodes.length"
                            @click="clearProductSelection"
                        >
                            Niciuna
                        </v-btn>
                    </div>
                </div>

                <v-data-table-server
                    v-if="!clientPaginationMode"
                    v-model="selectedProductCodes"
                    class="mt-0"
                    :headers="previewHeaders"
                    :items="tableRows"
                    :items-length="matchedTotalCount"
                    :page="candidatesPage"
                    :items-per-page="itemsPerPage"
                    :page-text="bulkPageText"
                    :items-per-page-options="itemsPerPageOptions"
                    :loading="loadingCandidates"
                    item-value="product_code"
                    show-select
                    density="compact"
                    @update:page="onCandidatesPageChange"
                    @update:items-per-page="onCandidatesItemsPerPageChange"
                >
                    <template v-slot:[`item.previous_price`]="{ item }">
                        {{ formatPrice(item.previous_price) }}
                    </template>
                    <template v-slot:[`item.next_price`]="{ item }">
                        <span :class="{ 'text-primary font-weight-medium': item.price_changed }">
                            {{ formatPrice(item.next_price) }}
                        </span>
                    </template>
                    <template #no-data>
                        <div class="text-medium-emphasis pa-4">
                            Nu există produse potrivite pentru filtrele selectate.
                        </div>
                    </template>
                </v-data-table-server>
                <v-data-table
                    v-else
                    v-model="selectedProductCodes"
                    class="mt-0"
                    :headers="previewHeaders"
                    :items="tableRows"
                    item-value="product_code"
                    show-select
                    density="compact"
                    v-model:page="clientTablePage"
                    :items-per-page="itemsPerPage"
                    :page-text="bulkPageText"
                    :items-per-page-options="itemsPerPageOptions"
                    :loading="loadingCandidates"
                    @update:items-per-page="itemsPerPage = $event"
                >
                    <template v-slot:[`item.previous_price`]="{ item }">
                        {{ formatPrice(item.previous_price) }}
                    </template>
                    <template v-slot:[`item.next_price`]="{ item }">
                        <span :class="{ 'text-primary font-weight-medium': item.price_changed }">
                            {{ formatPrice(item.next_price) }}
                        </span>
                    </template>
                    <template #no-data>
                        <div class="text-medium-emphasis pa-4">
                            Nu există produse potrivite pentru filtrele selectate.
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-card-actions class="d-flex flex-wrap ga-2 pa-4">
                <v-spacer />
                <v-btn
                    color="danger"
                    variant="elevated"
                    :disabled="saving || applying || isApplyBuildBusy"
                    @click="close"
                >
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="elevated"
                    style="color: white !important"
                    :disabled="!canApply || saving || applying || isApplyBuildBusy"
                    :loading="saving || isApplyBuildBusy"
                    @click="submitDrafts"
                >
                    Salvează în lucru
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    :disabled="!canApplyDirect || saving || applying || isApplyBuildBusy"
                    :loading="applying || isApplyBuildBusy"
                    :title="saveDirectDisabledReason"
                    @click="submitDirect"
                >
                    Salvează în listă
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import HierarchicalFilterSelect from '@/components/pricelist/HierarchicalFilterSelect.vue';
import { listFurnizorFilterOptions } from '@/data/furnizorNames';
import { getManufacturerCodes } from '@/data/manufacturerCodes';
import { SYSTEM_OPTIONS } from '@/data/productCategories';
import {
    getChapterOptionsForSystems,
    getSubchapterOptionsForSystems,
    listAllChapters,
    padSubchapterCode,
} from '@/data/productStructureLookups';
import {
    filterItemsForBulkEdit,
    buildBulkPreview,
    buildBulkPreviewAsync,
    itemLocksMarkupToReference,
    resolveItemsWithDraftOverlay,
} from '@/utils/priceListBulkApply';
import {
    BULK_CANDIDATE_FETCH_DEBOUNCE_MS,
    BULK_DIALOG_ITEMS_PER_PAGE_OPTIONS,
    BULK_DIALOG_PREVIEW_DEBOUNCE_MS,
    buildBulkCandidateQueryParams,
    bulkCriteriaForClientMatch,
    bulkCriteriaNeedsClientPagination,
    sanitizeBulkNumericInput,
    shouldBlockBulkNumericKey,
    syncBulkMatchedSelection,
} from '@/utils/priceListBulkDialogHelpers';
import {
    formatBulkEditValue,
    formatCurrentFieldValue,
    getAddableBulkFilterOptions,
    getBulkField,
    getEditableBulkFieldOptions,
    bulkEditsAllowDirectListSave,
} from '@/utils/priceListBulkFields';
import {
    ensureProductLookupsLoaded,
    fetchSubcategoriesForSystem,
} from '@/utils/productLookupApi';
import { diacriticInsensitiveFilter } from '@/utils/textSearch';
import {
    clientTablePageWindow,
    formatDataTablePageText,
    resolveServerPage,
} from '@/utils/dataTablePageText';
import { formatRonPrice, showAlertModal } from '@/utils/utils';
import {
    isAnulatProductStatus,
    normalizeProductStatus,
} from '@/data/productStatus';
import { buildCalculationRequiredBulkStatusMessage } from '@/utils/calculationRequiredRestriction';
import { findCalculationRequiredProduct } from '@/utils/priceListVerification';

function emptyCriteria() {
    return {
        search: '',
        systems: [],
        systemSubcategory: null,
        manufacturerCode: null,
        furnizorLabel: null,
        chapterCode: [],
        subchapterCode: null,
    };
}

function emptyValueForFilterField(field) {
    if (!field) {
        return null;
    }
    if (field.valueType === 'boolean') {
        return null;
    }
    if (field.valueType === 'systems') {
        return [];
    }
    if (field.valueType === 'chapter' && field.key === 'chapterCode') {
        return [];
    }
    return null;
}

let editRowSeq = 0;

function createEditRow(fieldKey = 'markupReference', value = '', applyMode = 'set') {
    editRowSeq += 1;
    return {
        id: `edit-${editRowSeq}`,
        fieldKey,
        value,
        applyMode,
    };
}

export default {
    name: 'PriceListBulkPropertyDialog',
    components: {
        HierarchicalFilterSelect,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: () => [],
        },
        /** Existing lista-în-lucru entries — bulk edits must base on these when present. */
        productDrafts: {
            type: Array,
            default: () => [],
        },
        selectedPriceList: {
            type: [Number, String, null],
            default: null,
        },
        priceLists: {
            type: Array,
            default: () => [],
        },
        pricingSettings: {
            type: Object,
            default: () => ({}),
        },
        measurementUnits: {
            type: Array,
            default: () => [],
        },
        saving: {
            type: Boolean,
            default: false,
        },
        applying: {
            type: Boolean,
            default: false,
        },
        /**
         * Server-side save progress from parent (after local payload build).
         * @type {{ message: string, current: number, total: number }|null}
         */
        saveProgress: {
            type: Object,
            default: null,
        },
        /**
         * One page of price-list products (server pagination).
         * @returns {Promise<{ items: Array, count: number, page: number }>}
         */
        fetchCandidatePage: {
            type: Function,
            default: null,
        },
        /**
         * Full filtered set — used when client-only filters force local pagination,
         * or when selecting / applying across all matches.
         */
        fetchCandidateItems: {
            type: Function,
            default: null,
        },
    },
    emits: ['update:modelValue', 'add-drafts', 'apply-direct'],
    data() {
        return {
            criteria: emptyCriteria(),
            editRows: [createEditRow('markupReference', '')],
            effectiveNote: '',
            selectedProductCodes: [],
            calculationRestrictedProducts: [],
            calculationRestrictedCheckSeq: 0,
            subcategoryOptions: [],
            loadingSubcategories: false,
            lookupsVersion: 0,
            diacriticInsensitiveFilter,
            activeExtraFilters: [],
            addFilterMenuOpen: false,
            filterToAdd: null,
            itemsPerPage: 25,
            itemsPerPageOptions: [...BULK_DIALOG_ITEMS_PER_PAGE_OPTIONS],
            previewCache: { rows: [], skipped: [], payloads: [], drafts: [] },
            previewBuildTimer: null,
            /** @type {{ message: string, current: number, total: number }|null} */
            applyBuildProgress: null,
            candidateItems: [],
            candidatesPage: 1,
            clientTablePage: 1,
            candidatesTotalCount: 0,
            itemsByCode: {},
            loadingCandidates: false,
            selectingAllMatching: false,
            candidateFetchTimer: null,
            candidateFetchSeq: 0,
            booleanFilterOptions: [
                { title: 'Da', value: true },
                { title: 'Nu', value: false },
            ],
            applyModeOptions: [
                { title: 'Setează', value: 'set' },
                { title: 'Majorare / micșorare %', value: 'multiplyPercent' },
            ],
            booleanEditOptions: [
                { title: 'Da', value: true },
                { title: 'Nu', value: false },
            ],
            previewHeaders: [
                { title: 'Cod', key: 'product_code', sortable: true },
                { title: 'Denumire', key: 'product_name', sortable: true },
                { title: 'Valoare curentă', key: 'previous_value', sortable: false },
                { title: 'Valoare nouă', key: 'next_value', sortable: false },
                {
                    title: 'Preț vânzare vechi cu TVA',
                    key: 'previous_price',
                    sortable: true,
                    align: 'end',
                },
                {
                    title: 'Preț vânzare nou cu TVA',
                    key: 'next_price',
                    sortable: true,
                    align: 'end',
                },
            ],
        };
    },
    computed: {
        editableFieldOptions() {
            return getEditableBulkFieldOptions();
        },
        availableEditFieldOptions() {
            const selected = new Set(
                this.editRows.map((row) => row.fieldKey).filter(Boolean),
            );
            return this.editableFieldOptions.filter((option) => !selected.has(option.value));
        },
        availableExtraFilterOptions() {
            const active = new Set(this.activeExtraFilters);
            return getAddableBulkFilterOptions().filter((option) => !active.has(option.value));
        },
        categoryOptions() {
            return SYSTEM_OPTIONS;
        },
        measurementUnitOptions() {
            const fromProp = (this.measurementUnits || []).map((unit) => ({
                text: unit.text || unit.value,
                value: unit.value || unit.text,
            }));
            if (fromProp.length) {
                return fromProp;
            }
            const units = new Set();
            for (const item of this.items || []) {
                if (item.measurement_unit) {
                    units.add(String(item.measurement_unit));
                }
            }
            return Array.from(units).sort().map((unit) => ({
                text: unit,
                value: unit,
            }));
        },
        manufacturerOptions() {
            void this.lookupsVersion;
            return getManufacturerCodes()
                .filter((entry) => String(entry?.code || '').trim())
                .map((entry) => {
                    const code = String(entry.code).trim();
                    const name = String(entry.name || '').trim();
                    return {
                        title: name ? `${code} – ${name}` : code,
                        value: code,
                    };
                })
                .sort((a, b) => a.title.localeCompare(b.title, 'ro'));
        },
        furnizorFilterOptions() {
            void this.lookupsVersion;
            // Full admin catalogs (same source as Produse header / create-edit),
            // not only labels present on the currently loaded page.
            return listFurnizorFilterOptions(undefined, getManufacturerCodes())
                .map((option) => option.value);
        },
        chapterOptions() {
            void this.lookupsVersion;
            const chapters = this.hasSystemFilter
                ? getChapterOptionsForSystems(this.criteria.systems)
                : listAllChapters().map((chapter) => ({
                    code: chapter.code,
                    name: chapter.name,
                    title: chapter.name
                        ? `${chapter.code} – ${chapter.name}`
                        : chapter.code,
                }));
            return chapters.map((option) => ({
                title: option.title
                    || (option.name ? `${option.code} – ${option.name}` : option.code),
                value: option.code,
            }));
        },
        subchapterOptions() {
            void this.lookupsVersion;
            const chapterCodes = Array.isArray(this.criteria.chapterCode)
                ? this.criteria.chapterCode
                : (this.criteria.chapterCode ? [this.criteria.chapterCode] : []);
            if (!chapterCodes.length) {
                return [];
            }
            const systems = this.hasSystemFilter
                ? this.criteria.systems
                : SYSTEM_OPTIONS.map((option) => option.value);
            const byCode = new Map();
            for (const chapterCode of chapterCodes) {
                for (const option of getSubchapterOptionsForSystems(systems, chapterCode)) {
                    if (!byCode.has(option.code)) {
                        byCode.set(option.code, {
                            title: option.title
                                || (option.name ? `${option.code} – ${option.name}` : option.code),
                            value: option.code,
                        });
                    }
                }
            }
            return Array.from(byCode.values()).sort((a, b) => (
                Number(a.value) - Number(b.value)
                || String(a.value).localeCompare(String(b.value), 'ro')
            ));
        },
        hasSystemFilter() {
            return Array.isArray(this.criteria.systems) && this.criteria.systems.length > 0;
        },
        hasChapterFilter() {
            if (Array.isArray(this.criteria.chapterCode)) {
                return this.criteria.chapterCode.length > 0;
            }
            return Boolean(this.criteria.chapterCode);
        },
        clientPaginationMode() {
            return bulkCriteriaNeedsClientPagination(this.normalizedCriteria);
        },
        pageMatchedItems() {
            const rows = Array.isArray(this.candidateItems) ? this.candidateItems : [];
            // API already applied furnizor/search/… — only keep client-only criteria
            // so a label mismatch cannot empty a valid server result set.
            return filterItemsForBulkEdit(
                rows,
                bulkCriteriaForClientMatch(this.normalizedCriteria),
            );
        },
        /** Live page rows with lista-în-lucru overlay for current values / recalc. */
        pageMatchedItemsWithDrafts() {
            return this.overlayItemsWithDrafts(this.pageMatchedItems);
        },
        /** @deprecated use pageMatchedItems / matchedTotalCount */
        matchedItems() {
            return this.pageMatchedItems;
        },
        matchedTotalCount() {
            if (this.clientPaginationMode) {
                return this.pageMatchedItems.length;
            }
            return Number(this.candidatesTotalCount) || 0;
        },
        filterParamsKey() {
            if (!this.modelValue || this.selectedPriceList == null) {
                return '';
            }
            return JSON.stringify({
                priceListId: this.selectedPriceList,
                clientMode: this.clientPaginationMode,
                params: buildBulkCandidateQueryParams(this.normalizedCriteria),
            });
        },
        candidateFetchKey() {
            if (!this.filterParamsKey) {
                return '';
            }
            return JSON.stringify({
                filter: this.filterParamsKey,
                page: this.clientPaginationMode ? 1 : this.candidatesPage,
                itemsPerPage: this.itemsPerPage,
            });
        },
        selectedPriceListObject() {
            return (this.priceLists || []).find(
                (entry) => entry.id === this.selectedPriceList,
            ) || (this.selectedPriceList != null ? { id: this.selectedPriceList } : null);
        },
        priceListIdsForApply() {
            return this.selectedPriceListObject ? [this.selectedPriceListObject] : [];
        },
        normalizedCriteria() {
            const criteria = { ...this.criteria };
            if (!this.hasSystemFilter) {
                criteria.systemSubcategory = '';
            }
            if (!this.hasChapterFilter) {
                criteria.subchapterCode = '';
            }
            if (!criteria.manufacturerCode) {
                delete criteria.manufacturerCode;
            }
            if (!criteria.furnizorLabel) {
                delete criteria.furnizorLabel;
            }
            if (Array.isArray(criteria.chapterCode)) {
                if (!criteria.chapterCode.length) {
                    delete criteria.chapterCode;
                }
            } else if (!criteria.chapterCode) {
                delete criteria.chapterCode;
            }
            if (!criteria.subchapterCode) {
                delete criteria.subchapterCode;
            }
            if (!criteria.systemSubcategory) {
                delete criteria.systemSubcategory;
            }
            for (const key of this.activeExtraFilters) {
                const value = criteria[key];
                if (value === null || value === undefined || value === '') {
                    delete criteria[key];
                    continue;
                }
                if (Array.isArray(value) && !value.length) {
                    delete criteria[key];
                }
            }
            // Drop inactive optional filters that may linger on the criteria object.
            const allowedExtra = new Set(this.activeExtraFilters);
            for (const key of Object.keys(criteria)) {
                if (
                    !['search', 'systems', 'systemSubcategory',
                        'manufacturerCode', 'furnizorLabel',
                        'chapterCode', 'subchapterCode'].includes(key)
                    && !allowedExtra.has(key)
                ) {
                    delete criteria[key];
                }
            }
            // Manufacturer filter uses code only (dropdown value).
            delete criteria.manufacturerName;
            return criteria;
        },
        statusEditIsAnulat() {
            return this.editRows.some((row) => (
                row.fieldKey === 'status'
                && isAnulatProductStatus(normalizeProductStatus(row.value))
            ));
        },
        calculationRestrictedCodeSet() {
            return new Set(
                this.calculationRestrictedProducts.map((info) => info.product_code),
            );
        },
        calculationRestrictedMessage() {
            return buildCalculationRequiredBulkStatusMessage(
                this.calculationRestrictedProducts,
            );
        },
        hasEditValue() {
            return this.validEditRows.length > 0
                || String(this.effectiveNote || '').trim() !== '';
        },
        validEditRows() {
            return this.editRows.filter((row) => {
                const field = getBulkField(row.fieldKey);
                if (!field || !row.fieldKey) {
                    return false;
                }
                if (field.valueType === 'boolean') {
                    return typeof row.value === 'boolean';
                }
                if (field.valueType === 'systems') {
                    return Array.isArray(row.value) && row.value.length > 0;
                }
                return String(row.value ?? '').trim() !== '';
            });
        },
        preview() {
            return this.previewCache;
        },
        skippedUnchangedCount() {
            return (this.preview.skipped || []).filter((entry) => (
                /neschimbat/i.test(String(entry?.reason || ''))
            )).length;
        },
        skippedIncompatibleCount() {
            return (this.preview.skipped || []).filter((entry) => (
                !/neschimbat/i.test(String(entry?.reason || ''))
            )).length;
        },
        previewBuildKey() {
            if (!this.validEditRows.length && !String(this.effectiveNote || '').trim()) {
                return '';
            }
            return JSON.stringify({
                codes: this.pageMatchedItems.map((item) => item.product_code),
                edits: this.validEditRows.map((row) => ({
                    fieldKey: row.fieldKey,
                    value: row.value,
                    applyMode: this.resolveApplyMode(row),
                })),
                effectiveNote: String(this.effectiveNote || '').trim(),
                furnizorLabel: this.criteria.furnizorLabel || '',
                priceListId: this.selectedPriceList,
                page: this.candidatesPage,
                // Rebuild when lista în lucru changes for codes on this page.
                draftFingerprint: (this.productDrafts || [])
                    .map((draft) => [
                        draft?.id,
                        draft?.payload?.product_code || draft?.sourceProductCode,
                        draft?.payload?.price,
                        draft?.updatedAt || draft?.createdAt,
                    ].join(':'))
                    .join('|'),
            });
        },
        bulkPageText() {
            if (this.clientPaginationMode) {
                return clientTablePageWindow(
                    this.tableRows.length,
                    this.clientTablePage,
                    this.itemsPerPage,
                ).text;
            }
            const visibleCount = this.tableRows.length;
            const fetchedCount = Array.isArray(this.candidateItems)
                ? this.candidateItems.length
                : visibleCount;
            const clientShrunk = visibleCount < fetchedCount;
            return formatDataTablePageText({
                total: clientShrunk || visibleCount === 0
                    ? visibleCount
                    : this.matchedTotalCount,
                visibleCount,
                page: clientShrunk ? 1 : this.candidatesPage,
                itemsPerPage: clientShrunk
                    ? Math.max(visibleCount, 1)
                    : this.itemsPerPage,
            });
        },
        tableRows() {
            if (this.hasEditValue && this.preview.rows.length) {
                return this.annotateCalculationRestrictedRows(this.preview.rows);
            }
            const fields = this.editRows
                .map((row) => getBulkField(row.fieldKey))
                .filter(Boolean);
            // When every match is "valoare neschimbată", still show the page so
            // 85% → 85% is visible with preț vânzare neschimbat (not an empty table).
            const unchangedSkipped = (this.preview.skipped || []).filter((entry) => (
                /neschimbat/i.test(String(entry?.reason || ''))
            ));
            if (
                this.hasEditValue
                && !this.preview.rows.length
                && unchangedSkipped.length
            ) {
                return this.annotateCalculationRestrictedRows(unchangedSkipped.map(({ item }) => ({
                    product_code: item.product_code,
                    product_name: item.product_name || item.name,
                    previous_value: fields.length
                        ? fields.map((field) => {
                            const label = formatCurrentFieldValue(
                                field,
                                item,
                                this.normalizedCriteria,
                            );
                            return fields.length > 1
                                ? `${field.label}: ${label}`
                                : label;
                        }).join('; ')
                        : '—',
                    next_value: fields.length
                        ? this.validEditRows.map((row) => {
                            const field = getBulkField(row.fieldKey);
                            if (!field) {
                                return '—';
                            }
                            const label = formatBulkEditValue(field, row.value);
                            return fields.length > 1
                                ? `${field.label}: ${label}`
                                : label;
                        }).join('; ')
                        : '—',
                    previous_price: item.price,
                    next_price: item.price,
                    price_changed: false,
                })));
            }
            // Always show matched products, even before a new value is entered.
            return this.annotateCalculationRestrictedRows(this.pageMatchedItemsWithDrafts.map((item) => ({
                product_code: item.product_code,
                product_name: item.product_name || item.name,
                previous_value: fields.length
                    ? fields.map((field) => {
                        const label = formatCurrentFieldValue(
                            field,
                            item,
                            this.normalizedCriteria,
                        );
                        return fields.length > 1
                            ? `${field.label}: ${label}`
                            : label;
                    }).join('; ')
                    : '—',
                next_value: this.hasEditValue ? '…' : '—',
                previous_price: item.price,
                next_price: item.price,
                price_changed: false,
            })));
        },
        selectedPreviewDrafts() {
            const selected = new Set(this.selectedProductCodes);
            return (this.preview.drafts || []).filter((draft) => {
                const code = draft?.sourceProductCode
                    || draft?.payload?.product_code
                    || draft?.payload?.old_product_code;
                return selected.has(code);
            });
        },
        selectedPreviewPayloads() {
            const selected = new Set(this.selectedProductCodes);
            return (this.preview.payloads || []).filter((payload) => {
                const code = payload?.product_code || payload?.old_product_code;
                return selected.has(code);
            });
        },
        canApply() {
            return this.hasEditValue
                && this.priceListIdsForApply.length > 0
                && this.selectedProductCodes.length > 0;
        },
        editsIncludeMarkupReference() {
            return this.validEditRows.some((row) => row.fieldKey === 'markupReference');
        },
        markupReferenceLockedItems() {
            if (!this.editsIncludeMarkupReference) {
                return [];
            }
            const label = this.criteria.furnizorLabel || '';
            const items = this.overlayItemsWithDrafts(
                this.selectedProductCodes
                    .map((code) => this.itemsByCode[code])
                    .filter(Boolean),
            );
            return items.filter((item) => itemLocksMarkupToReference(item, label));
        },
        markupReferenceLockedMessage() {
            const count = this.markupReferenceLockedItems.length;
            if (!count) {
                return '';
            }
            const noun = count === 1 ? 'produs are' : 'produse au';
            return `${count} ${noun} „Adaos = referință”. La acestea se actualizează și adaosul actual și prețul de listă. Salvează în listă le omite; folosește Salvează în lucru.`;
        },
        canApplyDirect() {
            if (
                !this.canApply
                || !bulkEditsAllowDirectListSave(this.validEditRows, {
                    effectiveNote: this.effectiveNote,
                })
            ) {
                return false;
            }
            const selectedCount = this.selectedProductCodes.length;
            const lockedCount = this.markupReferenceLockedItems.length;
            return !(selectedCount > 0 && lockedCount === selectedCount);
        },
        isApplyBuildBusy() {
            return Boolean(this.applyBuildProgress);
        },
        activeProgress() {
            return this.saveProgress || this.applyBuildProgress || null;
        },
        activeProgressPercent() {
            const progress = this.activeProgress;
            if (!progress?.total) {
                return 0;
            }
            return Math.min(100, Math.round((progress.current / progress.total) * 100));
        },
        saveDirectDisabledReason() {
            if (this.canApplyDirect) {
                return '';
            }
            if (!this.canApply) {
                return '';
            }
            const selectedCount = this.selectedProductCodes.length;
            if (
                this.editsIncludeMarkupReference
                && selectedCount > 0
                && this.markupReferenceLockedItems.length === selectedCount
            ) {
                return 'Produsele selectate au „Adaos = referință”. Se actualizează și adaosul actual și prețul. Salvează-le în lista în lucru.';
            }
            return 'Salvarea directă în listă este disponibilă pentru Detalii produs, note furnizor, termen de plată și adaos referință.';
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.resetForm();
                void this.bootstrapLookups();
            } else {
                this.clearPreviewBuildTimer();
            }
        },
        previewBuildKey: {
            immediate: true,
            handler(key) {
                this.schedulePreviewBuild(key);
            },
        },
        tableRows() {
            if (!this.clientPaginationMode) {
                return;
            }
            const next = clientTablePageWindow(
                this.tableRows.length,
                this.clientTablePage,
                this.itemsPerPage,
            ).page;
            if (this.clientTablePage !== next) {
                this.clientTablePage = next;
            }
        },
        pageMatchedItems: {
            handler(items) {
                // Keep cross-page selection; only drop codes that disappear from
                // the full match set when we know the full set (client mode).
                if (this.clientPaginationMode) {
                    this.selectedProductCodes = syncBulkMatchedSelection(
                        this.selectedProductCodes,
                        items,
                    );
                }
            },
        },
        selectedProductCodes: {
            handler() {
                void this.refreshCalculationRestrictions();
            },
        },
        statusEditIsAnulat() {
            void this.refreshCalculationRestrictions();
        },
        filterParamsKey(newKey, oldKey) {
            if (!newKey || newKey === oldKey) {
                return;
            }
            if (this.candidatesPage !== 1) {
                this.candidatesPage = 1;
            }
            this.clientTablePage = 1;
            // Filters define a new match set — drop stale cross-page selection.
            this.selectedProductCodes = [];
        },
        candidateFetchKey: {
            immediate: true,
            handler(key) {
                this.scheduleCandidateFetch(key);
            },
        },
    },
    beforeUnmount() {
        this.clearPreviewBuildTimer();
        this.clearCandidateFetchTimer();
    },
    methods: {
        getBulkField,
        /**
         * Prefer lista-în-lucru builder values when a product already has a draft.
         * @param {Array<Object>} items
         * @returns {Array<Object>}
         */
        overlayItemsWithDrafts(items) {
            return resolveItemsWithDraftOverlay(
                items,
                this.productDrafts,
                { includeOrphanDrafts: false },
            );
        },
        rememberItems(rows) {
            const next = { ...this.itemsByCode };
            for (const item of rows || []) {
                const code = item?.product_code;
                if (code) {
                    next[code] = item;
                }
            }
            this.itemsByCode = next;
        },
        clearPreviewBuildTimer() {
            if (this.previewBuildTimer != null) {
                clearTimeout(this.previewBuildTimer);
                this.previewBuildTimer = null;
            }
        },
        clearCandidateFetchTimer() {
            if (this.candidateFetchTimer != null) {
                clearTimeout(this.candidateFetchTimer);
                this.candidateFetchTimer = null;
            }
        },
        scheduleCandidateFetch(key) {
            this.clearCandidateFetchTimer();
            if (!key) {
                this.candidateFetchSeq += 1;
                this.candidateItems = [];
                this.candidatesTotalCount = 0;
                this.loadingCandidates = false;
                return;
            }
            this.candidateFetchTimer = setTimeout(() => {
                this.candidateFetchTimer = null;
                void this.loadCandidateItems(key);
            }, BULK_CANDIDATE_FETCH_DEBOUNCE_MS);
        },
        onCandidatesPageChange(page) {
            const next = Number(page) || 1;
            if (next === this.candidatesPage) {
                return;
            }
            this.candidatesPage = next;
        },
        onCandidatesItemsPerPageChange(size) {
            const next = Number(size);
            this.itemsPerPage = Number.isFinite(next) && next > 0
                ? Math.min(next, 100)
                : 25;
            this.candidatesPage = 1;
            this.clientTablePage = 1;
        },
        async loadCandidateItems(expectedKey) {
            const params = buildBulkCandidateQueryParams(this.normalizedCriteria);
            const seq = (this.candidateFetchSeq += 1);
            this.loadingCandidates = true;
            try {
                if (this.clientPaginationMode) {
                    if (typeof this.fetchCandidateItems !== 'function') {
                        this.candidateItems = Array.isArray(this.items) ? [...this.items] : [];
                        this.candidatesTotalCount = this.candidateItems.length;
                        return;
                    }
                    const rows = await this.fetchCandidateItems(params);
                    if (seq !== this.candidateFetchSeq || this.candidateFetchKey !== expectedKey) {
                        return;
                    }
                    const list = Array.isArray(rows) ? rows : [];
                    this.candidateItems = list;
                    this.candidatesTotalCount = list.length;
                    this.rememberItems(list);
                    return;
                }
                if (typeof this.fetchCandidatePage !== 'function') {
                    const fallback = Array.isArray(this.items) ? [...this.items] : [];
                    this.candidateItems = fallback;
                    this.candidatesTotalCount = fallback.length;
                    this.rememberItems(fallback);
                    return;
                }
                const result = await this.fetchCandidatePage({
                    params,
                    page: this.candidatesPage,
                    itemsPerPage: this.itemsPerPage,
                });
                if (seq !== this.candidateFetchSeq || this.candidateFetchKey !== expectedKey) {
                    return;
                }
                let list = Array.isArray(result?.items) ? result.items : [];
                let total = Number(result?.count) || list.length;
                const resolved = resolveServerPage({
                    requestedPage: Number(result?.page) || this.candidatesPage,
                    total,
                    pageSize: this.itemsPerPage,
                });
                if (
                    resolved.shouldRefetch
                    && typeof this.fetchCandidatePage === 'function'
                ) {
                    const retried = await this.fetchCandidatePage({
                        params,
                        page: resolved.page,
                        itemsPerPage: this.itemsPerPage,
                    });
                    if (seq !== this.candidateFetchSeq || this.candidateFetchKey !== expectedKey) {
                        return;
                    }
                    list = Array.isArray(retried?.items) ? retried.items : [];
                    total = Number(retried?.count) || list.length;
                }
                if (seq !== this.candidateFetchSeq || this.candidateFetchKey !== expectedKey) {
                    return;
                }
                this.candidateItems = list;
                this.candidatesTotalCount = total;
                this.candidatesPage = resolved.page;
                this.rememberItems(list);
            } catch (_error) {
                if (seq !== this.candidateFetchSeq) {
                    return;
                }
                const fallback = Array.isArray(this.items) ? [...this.items] : [];
                this.candidateItems = fallback;
                this.candidatesTotalCount = fallback.length;
            } finally {
                if (seq === this.candidateFetchSeq) {
                    this.loadingCandidates = false;
                }
            }
        },
        rebuildPreviewNow() {
            this.clearPreviewBuildTimer();
            if (!this.validEditRows.length && !String(this.effectiveNote || '').trim()) {
                this.previewCache = { rows: [], skipped: [], payloads: [], drafts: [] };
                return;
            }
            // Preview the current page (cheap). Apply uses selected codes + cache.
            this.previewCache = buildBulkPreview({
                items: this.pageMatchedItemsWithDrafts,
                edits: this.validEditRows.map((row) => ({
                    fieldKey: row.fieldKey,
                    value: row.value,
                    applyMode: this.resolveApplyMode(row),
                })),
                priceListIds: this.priceListIdsForApply,
                pricingSettings: this.pricingSettings,
                effectiveNote: this.effectiveNote,
                furnizorLabel: this.criteria.furnizorLabel || '',
            });
        },
        schedulePreviewBuild(key) {
            this.clearPreviewBuildTimer();
            if (!key) {
                this.previewCache = { rows: [], skipped: [], payloads: [], drafts: [] };
                return;
            }
            // Debounce expensive per-product pricing while typing edit values.
            this.previewBuildTimer = setTimeout(() => {
                this.previewBuildTimer = null;
                this.rebuildPreviewNow();
            }, BULK_DIALOG_PREVIEW_DEBOUNCE_MS);
        },
        formatPrice(value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return formatRonPrice(value);
        },
        showsApplyModeSelect(fieldKey) {
            const field = getBulkField(fieldKey);
            return Boolean(field?.supportsMultiplyPercent && !field?.fixedApplyMode);
        },
        isMultiplyPercentEdit(editRow) {
            return this.resolveApplyMode(editRow) === 'multiplyPercent';
        },
        isBulkNumericValueField(editRow) {
            return getBulkField(editRow?.fieldKey)?.valueType === 'number';
        },
        onBulkNumericKeydown(event, editIndex) {
            const row = this.editRows[editIndex];
            if (shouldBlockBulkNumericKey(event, row?.value)) {
                event.preventDefault();
            }
        },
        onBulkNumericValueInput(editIndex, rawValue) {
            const row = this.editRows[editIndex];
            if (!row) {
                return;
            }
            const next = sanitizeBulkNumericInput(rawValue);
            const rawText = String(rawValue ?? '');
            // Same sanitized value but raw still has rejected chars (3rd decimal,
            // period, etc.) → remount the field so the DOM matches the model.
            if (row.value === next) {
                if (rawText !== next) {
                    this.editRows.splice(editIndex, 1, {
                        ...row,
                        value: next,
                        _numericRev: (row._numericRev || 0) + 1,
                    });
                }
                return;
            }
            this.editRows.splice(editIndex, 1, {
                ...row,
                value: next,
            });
        },
        resolveApplyMode(editRow) {
            const field = getBulkField(editRow?.fieldKey);
            if (field?.fixedApplyMode === 'multiplyPercent') {
                return 'multiplyPercent';
            }
            return editRow?.applyMode === 'multiplyPercent' ? 'multiplyPercent' : 'set';
        },
        selectPageProducts() {
            const pageCodes = this.pageMatchedItems
                .map((item) => item.product_code)
                .filter(Boolean);
            this.selectedProductCodes = [...new Set([
                ...this.selectedProductCodes,
                ...pageCodes,
            ])];
        },
        async selectAllMatchingProducts() {
            if (this.clientPaginationMode) {
                this.selectedProductCodes = this.pageMatchedItems
                    .map((item) => item.product_code)
                    .filter(Boolean);
                return;
            }
            if (typeof this.fetchCandidateItems !== 'function') {
                this.selectPageProducts();
                return;
            }
            this.selectingAllMatching = true;
            try {
                const params = buildBulkCandidateQueryParams(this.normalizedCriteria);
                const rows = await this.fetchCandidateItems(params);
                const matched = filterItemsForBulkEdit(
                    Array.isArray(rows) ? rows : [],
                    bulkCriteriaForClientMatch(this.normalizedCriteria),
                );
                this.rememberItems(matched);
                this.selectedProductCodes = matched
                    .map((item) => item.product_code)
                    .filter(Boolean);
            } catch (_error) {
                this.selectPageProducts();
            } finally {
                this.selectingAllMatching = false;
            }
        },
        clearProductSelection() {
            this.selectedProductCodes = [];
        },
        /**
         * Resolve full item rows for the current selection (cache + optional fetch).
         * @returns {Promise<Array<Object>>}
         */
        async resolveSelectedItems() {
            const selected = this.selectedProductCodes.filter(Boolean);
            if (!selected.length) {
                return [];
            }
            const missing = selected.filter((code) => !this.itemsByCode[code]);
            if (missing.length && typeof this.fetchCandidateItems === 'function') {
                try {
                    const params = buildBulkCandidateQueryParams(this.normalizedCriteria);
                    const rows = await this.fetchCandidateItems(params);
                    this.rememberItems(filterItemsForBulkEdit(
                        Array.isArray(rows) ? rows : [],
                        bulkCriteriaForClientMatch(this.normalizedCriteria),
                    ));
                } catch (_error) {
                    // Use whatever is already cached / on the current page.
                }
            }
            return this.overlayItemsWithDrafts(
                selected
                    .map((code) => this.itemsByCode[code])
                    .filter(Boolean),
            );
        },
        editableFieldOptionsForRow(editIndex) {
            const selectedElsewhere = new Set(
                this.editRows
                    .filter((_row, index) => index !== editIndex)
                    .map((row) => row.fieldKey)
                    .filter(Boolean),
            );
            return this.editableFieldOptions.filter(
                (option) => !selectedElsewhere.has(option.value),
            );
        },
        defaultValueForField(field) {
            if (!field) {
                return '';
            }
            if (field.isListExchangeRate) {
                const current = this.pricingSettings?.[field.key];
                return current != null && current !== '' ? String(current) : '';
            }
            if (field.valueType === 'boolean') {
                return true;
            }
            if (field.valueType === 'systems') {
                return [];
            }
            if (field.valueType === 'select' && field.options?.length) {
                return field.options[0].value;
            }
            if (field.valueType === 'measurementUnit') {
                return this.measurementUnitOptions[0]?.value || '';
            }
            if (field.valueType === 'manufacturer') {
                return this.manufacturerOptions[0]?.value || '';
            }
            if (field.valueType === 'chapter') {
                return this.chapterOptions[0]?.value || '';
            }
            return '';
        },
        onEditFieldKeyChange(editIndex, fieldKey) {
            const row = this.editRows[editIndex];
            if (!row || row.fieldKey === fieldKey) {
                return;
            }
            const field = getBulkField(fieldKey);
            this.editRows.splice(editIndex, 1, {
                ...row,
                fieldKey,
                value: this.defaultValueForField(field),
                applyMode: field?.fixedApplyMode === 'multiplyPercent'
                    ? 'multiplyPercent'
                    : (field?.supportsMultiplyPercent ? (row.applyMode || 'set') : 'set'),
            });
        },
        addEditRow() {
            const nextOption = this.availableEditFieldOptions[0];
            if (!nextOption) {
                return;
            }
            const field = getBulkField(nextOption.value);
            this.editRows = [
                ...this.editRows,
                createEditRow(
                    nextOption.value,
                    this.defaultValueForField(field),
                    field?.fixedApplyMode === 'multiplyPercent' ? 'multiplyPercent' : 'set',
                ),
            ];
        },
        removeEditRow(editIndex) {
            if (this.editRows.length <= 1) {
                return;
            }
            this.editRows = this.editRows.filter((_row, index) => index !== editIndex);
        },
        onFilterToAdd(fieldKey) {
            if (!fieldKey || this.activeExtraFilters.includes(fieldKey)) {
                this.filterToAdd = null;
                return;
            }
            const field = getBulkField(fieldKey);
            this.activeExtraFilters = [...this.activeExtraFilters, fieldKey];
            this.criteria = {
                ...this.criteria,
                [fieldKey]: emptyValueForFilterField(field),
            };
            this.filterToAdd = null;
            this.addFilterMenuOpen = false;
        },
        removeExtraFilter(fieldKey) {
            this.activeExtraFilters = this.activeExtraFilters.filter((key) => key !== fieldKey);
            if (Object.prototype.hasOwnProperty.call(this.criteria, fieldKey)) {
                const next = { ...this.criteria };
                delete next[fieldKey];
                this.criteria = next;
            }
        },
        async bootstrapLookups() {
            try {
                await ensureProductLookupsLoaded();
            } catch (_error) {
                // Static fallbacks remain available.
            }
            this.lookupsVersion += 1;
            await this.loadSubcategoryOptions();
            this.pruneChapterFilterSelection();
            this.pruneSubchapterFilterSelection();
        },
        async onSystemsFilterChange() {
            await this.loadSubcategoryOptions();
            this.pruneChapterFilterSelection();
            this.pruneSubchapterFilterSelection();
        },
        onChapterFilterChange() {
            this.pruneSubchapterFilterSelection();
        },
        pruneChapterFilterSelection() {
            const selected = Array.isArray(this.criteria.chapterCode)
                ? this.criteria.chapterCode
                : (this.criteria.chapterCode ? [this.criteria.chapterCode] : []);
            if (!selected.length) {
                this.criteria.chapterCode = [];
                return;
            }
            const allowed = new Set(this.chapterOptions.map((option) => String(option.value)));
            const next = selected.filter((code) => allowed.has(String(code)));
            if (next.length !== selected.length) {
                this.criteria.chapterCode = next;
                this.criteria.subchapterCode = null;
            }
        },
        pruneSubchapterFilterSelection() {
            const selected = String(this.criteria.subchapterCode || '').trim();
            if (!selected) {
                return;
            }
            const stillValid = this.subchapterOptions.some(
                (option) => String(option.value) === selected
                    || padSubchapterCode(option.value) === padSubchapterCode(selected),
            );
            if (!stillValid) {
                this.criteria.subchapterCode = null;
            }
        },
        async loadSubcategoryOptions() {
            if (!this.hasSystemFilter) {
                this.subcategoryOptions = [];
                return;
            }
            this.loadingSubcategories = true;
            try {
                const optionMap = new Map();
                for (const system of this.criteria.systems) {
                    const options = await fetchSubcategoriesForSystem(system);
                    for (const option of options) {
                        if (option?.value) {
                            optionMap.set(option.value, {
                                title: option.title || option.value,
                                value: option.value,
                            });
                        }
                    }
                }
                this.subcategoryOptions = Array.from(optionMap.values()).sort((a, b) =>
                    a.title.localeCompare(b.title, 'ro'),
                );
                const selectedSub = String(this.criteria.systemSubcategory || '').trim();
                if (
                    selectedSub
                    && !this.subcategoryOptions.some((option) => option.value === selectedSub)
                ) {
                    this.criteria.systemSubcategory = null;
                }
            } finally {
                this.loadingSubcategories = false;
            }
        },
        resetForm() {
            this.clearPreviewBuildTimer();
            this.clearCandidateFetchTimer();
            this.candidateFetchSeq += 1;
            this.candidateItems = [];
            this.candidatesPage = 1;
            this.clientTablePage = 1;
            this.candidatesTotalCount = 0;
            this.itemsByCode = {};
            this.loadingCandidates = false;
            this.selectingAllMatching = false;
            this.previewCache = { rows: [], skipped: [], payloads: [], drafts: [] };
            this.applyBuildProgress = null;
            this.criteria = emptyCriteria();
            this.activeExtraFilters = [];
            this.addFilterMenuOpen = false;
            this.filterToAdd = null;
            this.subcategoryOptions = [];
            this.effectiveNote = '';
            this.selectedProductCodes = [];
            this.calculationRestrictedProducts = [];
            this.calculationRestrictedCheckSeq += 1;
            this.itemsPerPage = 25;
            const defaultField = getBulkField('markupReference');
            this.editRows = [
                createEditRow('markupReference', this.defaultValueForField(defaultField)),
            ];
        },
        close() {
            this.$emit('update:modelValue', false);
        },
        buildPreviewForItems(items, { includeDrafts = true } = {}) {
            return buildBulkPreview({
                items: this.overlayItemsWithDrafts(items),
                edits: this.validEditRows.map((row) => ({
                    fieldKey: row.fieldKey,
                    value: row.value,
                    applyMode: this.resolveApplyMode(row),
                })),
                priceListIds: this.priceListIdsForApply,
                pricingSettings: this.pricingSettings,
                effectiveNote: this.effectiveNote,
                furnizorLabel: this.criteria.furnizorLabel || '',
                includeDrafts,
            });
        },
        /**
         * Chunked async build so large selections (1000+) stay responsive.
         * @param {Array<Object>} items
         * @param {{ includeDrafts?: boolean, progressMessage?: string }} [options]
         */
        async buildPreviewForItemsAsync(items, options = {}) {
            const includeDrafts = options.includeDrafts !== false;
            const total = Array.isArray(items) ? items.length : 0;
            this.applyBuildProgress = {
                message: options.progressMessage || 'Se pregătesc modificările…',
                current: 0,
                total,
            };
            try {
                return await buildBulkPreviewAsync(
                    {
                        items: this.overlayItemsWithDrafts(items),
                        edits: this.validEditRows.map((row) => ({
                            fieldKey: row.fieldKey,
                            value: row.value,
                            applyMode: this.resolveApplyMode(row),
                        })),
                        priceListIds: this.priceListIdsForApply,
                        pricingSettings: this.pricingSettings,
                        effectiveNote: this.effectiveNote,
                        furnizorLabel: this.criteria.furnizorLabel || '',
                        includeDrafts,
                    },
                    {
                        onProgress: (current, progressTotal) => {
                            this.applyBuildProgress = {
                                message: options.progressMessage
                                    || 'Se pregătesc modificările…',
                                current,
                                total: progressTotal,
                            };
                        },
                    },
                );
            } finally {
                this.applyBuildProgress = null;
            }
        },
        annotateCalculationRestrictedRows(rows) {
            if (!this.calculationRestrictedCodeSet.size) {
                return rows;
            }
            return rows.map((row) => {
                if (!this.calculationRestrictedCodeSet.has(row.product_code)) {
                    return row;
                }
                return {
                    ...row,
                    next_value: 'Nu se actualizează — folosit în formulele de calcul',
                };
            });
        },
        async refreshCalculationRestrictions() {
            await this.collectAnulatRestrictedProducts(this.selectedProductCodes);
        },
        /**
         * Selected products that cannot move to Anulat because offer formulas use them.
         * @param {string[]} codes
         * @returns {Promise<Array<{ product_code: string, catalogs?: string[] }>>}
         */
        async collectAnulatRestrictedProducts(codes) {
            const seq = this.calculationRestrictedCheckSeq + 1;
            this.calculationRestrictedCheckSeq = seq;
            if (!this.statusEditIsAnulat) {
                this.calculationRestrictedProducts = [];
                return [];
            }
            const found = [];
            for (const code of codes || []) {
                const info = await findCalculationRequiredProduct(code);
                if (info) {
                    found.push(info);
                }
            }
            if (seq !== this.calculationRestrictedCheckSeq) {
                return found;
            }
            this.calculationRestrictedProducts = found;
            return found;
        },
        warnCalculationRestrictedProducts(products) {
            const message = buildCalculationRequiredBulkStatusMessage(products);
            if (!message) {
                return;
            }
            showAlertModal(this.$store, message, 'warning', 12000);
        },
        async submitDrafts() {
            if (!this.canApply || this.isApplyBuildBusy) {
                return;
            }
            const items = await this.resolveSelectedItems();
            if (!items.length) {
                return;
            }
            const preview = await this.buildPreviewForItemsAsync(items, {
                includeDrafts: true,
                progressMessage: 'Se pregătesc drafturile…',
            });
            const selected = new Set(this.selectedProductCodes);
            const blocked = await this.collectAnulatRestrictedProducts(
                [...selected],
            );
            const blockedCodes = new Set(blocked.map((info) => info.product_code));
            this.warnCalculationRestrictedProducts(blocked);
            const drafts = (preview.drafts || []).filter((draft) => {
                const code = draft?.sourceProductCode
                    || draft?.payload?.product_code
                    || draft?.payload?.old_product_code;
                return selected.has(code) && !blockedCodes.has(code);
            });
            if (!drafts.length) {
                return;
            }
            this.$emit('add-drafts', drafts, {
                pricingSettingsPatch: preview.pricingSettingsPatch || null,
            });
        },
        async submitDirect() {
            if (!this.canApplyDirect || this.isApplyBuildBusy) {
                return;
            }
            const items = await this.resolveSelectedItems();
            if (!items.length) {
                return;
            }
            // Direct list save: skip draft/builderState clones (major speedup).
            const preview = await this.buildPreviewForItemsAsync(items, {
                includeDrafts: false,
                progressMessage: 'Se pregătesc actualizările…',
            });
            const selected = new Set(this.selectedProductCodes);
            const blocked = await this.collectAnulatRestrictedProducts(
                [...selected],
            );
            const blockedCodes = new Set(blocked.map((info) => info.product_code));
            this.warnCalculationRestrictedProducts(blocked);
            const lockedCodes = new Set();
            if (this.editsIncludeMarkupReference) {
                const label = this.criteria.furnizorLabel || '';
                items.forEach((item) => {
                    const code = String(item?.product_code || '').trim();
                    if (code && itemLocksMarkupToReference(item, label)) {
                        lockedCodes.add(code);
                    }
                });
                if (lockedCodes.size) {
                    const noun = lockedCodes.size === 1 ? 'produs are' : 'produse au';
                    showAlertModal(
                        this.$store,
                        `${lockedCodes.size} ${noun} „Adaos = referință” și nu intră la Salvează în listă. La ele se schimbă și adaosul actual și prețul — folosește Salvează în lucru.`,
                        'warning',
                        12000,
                    );
                }
            }
            const payloads = (preview.payloads || []).filter((payload) => {
                const code = payload?.product_code || payload?.old_product_code;
                return selected.has(code)
                    && !blockedCodes.has(code)
                    && !lockedCodes.has(code);
            });
            if (!payloads.length) {
                return;
            }
            this.$emit('apply-direct', payloads, {
                pricingSettingsPatch: preview.pricingSettingsPatch || null,
            });
        },
    },
};
</script>
